import { FormikHelpers, useFormik } from "formik";
import querystring from "querystring";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import * as Yup from "yup";

import { authApi, merchantApi, verifyApi } from "@api";
import { ComponentUserSetting, ComponentUserSettingSave } from "@components";
import {
    LINE_ACCESS_URL,
    LINE_SCOPE,
    PATH_USER_VERIFY,
    PATH_WELCOME,
    _10MB,
    enumExternalAuthType,
    enumUseSetting,
} from "@configs";
import { IExternalAuthState, IUserSetting, IVerify } from "@interfaces";
import { resetAuth, selectAuth, selectMerchant, setLoading } from "@redux";
import { RenderMeta, formatMessage, useNotify } from "@utils";
import { useYup } from "@validations";
import dayjs from "dayjs";

export const PageUserSetting = () => {
    //page hooks

    const dispatch = useDispatch();
    const { error, success } = useNotify();
    const { YubSUserSetting } = useYup();
    const { t } = useTranslation();
    const history = useHistory();
    //redux state
    const { userInfo } = useSelector(selectAuth);
    const { lineIntegration } = useSelector(selectMerchant);

    //page state
    const [title, setTitle] = useState<enumUseSetting | "">("");
    const [errorPhone, setError] = useState<string>();
    const [email, setEmail] = useState<string>("");

    //page variable
    const state: IExternalAuthState = {
        type: enumExternalAuthType.LINK_LINE,
    };
    const contact = userInfo?.contact;

    const init: IUserSetting = {
        avatar: "",
        fullName: "",
        fName: "",
        lName: "",
        dateOfBirth: "",
        phoneNumber: "",
        phone: "",
        email: "",
    };

    const [initialValue, setInitialValue] = useState<IUserSetting>(init);

    useEffect(() => {
        if (userInfo?.contact) {
            setInitialValue({
                avatar: userInfo?.avatar || "",
                fullName: userInfo?.contact?.fullName || "",
                fName: userInfo?.contact?.firstName,
                lName: userInfo?.contact?.lastName,
                dateOfBirth: userInfo?.contact?.dateOfBirth || "",
                phoneNumber: userInfo?.contact?.phoneNumber || "",
                phone: userInfo?.contact?.phoneNumber || "",
                email: userInfo?.contact?.email || "",
            });
            setEmail(userInfo?.contact?.email);
        }
    }, [userInfo?.contact]);

    const registerSchema = Yup.object().shape(YubSUserSetting);
    const lineRedirectUrl = window.location.origin + "/line-login";

    const handleChangeName = (e: any) => {
        setFieldValue(e.target.name, e.target.value);
    };

    const handleUploadImage = async (e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        dispatch(setLoading(true));
        //@ts-ignore
        const values = (e?.target.files[0] as File) || "";
        if (values.size >= _10MB) {
            error(t("validation.image.max_size"));
            dispatch(setLoading(false));
            return;
        }
        const formData = new FormData();
        formData.append("file", values);
        try {
            const res = await merchantApi.uploadImage(formData);
            await authApi.updateProfile({
                avatar: res.data.publicUrl,
            });
            setFieldValue("avatar", res.data.publicUrl);
        } catch (err: any) {
            // const data = err.response.data;
            // const mess = Array.isArray(data.errors) ? data.errors[0] : data.message;
            error(formatMessage(err.response));
        } finally {
            dispatch(setLoading(false));
        }
    };

    const handleVerilyEmail = async (values: IVerify) => {
        await null;
        if ((values.email || "").trim() === contact?.email) {
            setTitle("");
            return;
        } else {
            try {
                dispatch(setLoading(true));
                const res = await authApi.updateProfile({
                    email: values.email,
                });
                success(t("page.update_email_success"));
                setTitle("");
            } catch (err: any) {
                if (err.response) {
                    error(formatMessage(err.response));
                }
            }
        }

        // if (userInfo?.emailVerified && (values.email || "").trim() === contact?.email) {
        //     setTitle("");
        //     return;
        // }
        // try {
        //     dispatch(setLoading(true));
        //     const res = await verifyApi.updateEmail(values);
        //     if (res) {
        //         window.sessionStorage.setItem("email", values.email || "");
        //         history.push(PATH_USER_VERIFY + "/" + enumUseSetting.EMAIL);
        //         success(t("message.email_send"));
        //     }
        // } catch (errors: any) {
        //     // const { message } = errors.response.data;
        //     if (errors.response) {
        //         error(formatMessage(errors.response));
        //     } else {
        //         error(t("message.email_correct"));
        //     }
        // } finally {
        //     dispatch(setLoading(false));
        // }
    };

    const handleVerilyPhone = async (values: IVerify) => {
        if (userInfo?.phoneVerified && (values.phoneNumber || "").trim() === contact?.phoneNumber) {
            setTitle("");
            return;
        }
        try {
            dispatch(setLoading(true));
            const res = await verifyApi.updatePhone(values);
            if (res) {
                window.sessionStorage.setItem("phoneNumber", values.phoneNumber || "");
                history.push(PATH_USER_VERIFY + "/" + enumUseSetting.PHONE_NUMBER);
                success(t("message.phone_send"));
            }
        } catch (errors: any) {
            // const { message } = errors.response.data;
            if (errors.response) {
                error(formatMessage(errors.response));
            } else {
                error(t("message.phone_correct"));
            }
        } finally {
            dispatch(setLoading(false));
        }
    };
    function normalizePhone(phone: string | undefined): string {
        let clean = phone?.replace(/\s+/g, "") || "";
        clean = clean.replace(/^(?:\+66)+/, "+66");

        return clean;
    }

    const handleSaveSetting = async (
        values: IUserSetting,
        { setSubmitting }: FormikHelpers<IUserSetting>
    ) => {
        if (errorPhone) return;
        if (!values.phoneNumber) {
            setError(t("validation.isNumber"));
            return;
        }
        if (title === enumUseSetting.EMAIL) {
            handleVerilyEmail({
                email: values.email || "",
            });
            return;
        }
        if (title === enumUseSetting.PHONE_NUMBER) {
            handleVerilyPhone({
                phoneNumber: normalizePhone(values.phone),
            });
            return;
        }

        try {
            setSubmitting(true);
            dispatch(setLoading(true));
            const res = await authApi.updateProfile({
                ...values,
                dateOfBirth: values.dateOfBirth || "",
                phoneNumber: values.phone,
                avatar: values.avatar || undefined,
            });
            if (res.data.message) {
                success(res.data.message);
            } else {
                success(t("message.update.success"));
            }
            setFieldValue("phoneNumber", values.phone || "");
        } catch (errors: any) {
            // const errorsResponse = errors.response.data.errors;
            if (errors.response) {
                error(formatMessage(errors.response));
            } else {
                error(t("message.update.fail"));
            }
        } finally {
            setTitle("");
            setSubmitting(false);
            dispatch(setLoading(false));
        }
    };

    const handleChangeTitle = (title: enumUseSetting | "") => {
        setTitle(title);
    };
    const handleLogout = async () => {
        await dispatch(resetAuth());
        await history.push(PATH_WELCOME);
        await sessionStorage.removeItem("popupHomePage");
    };
    const {
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        errors,
        touched,
        setFieldValue,
        setFieldTouched,
    } = useFormik({
        initialValues: initialValue,
        validationSchema: registerSchema,
        onSubmit: handleSaveSetting,
        enableReinitialize: true,
    });

    const prevTitleRef = useRef<enumUseSetting | "">("");

    useEffect(() => {
        // If user cancels phone edit (back), restore the last known phone from profile.
        const originalPhone = contact?.phoneNumber ?? "";
        if (prevTitleRef.current === enumUseSetting.PHONE_NUMBER && title === "") {
            setFieldValue("phoneNumber", originalPhone);
            setFieldValue("phone", originalPhone);
        }
        prevTitleRef.current = title;
    }, [title, contact?.phoneNumber, setFieldValue]);

    const handleChangeDate = (date: dayjs.Dayjs | null, dateString: string | string[]) => {
        const nextValue = typeof dateString === "string" && dateString ? dateString : "";
        setFieldValue("dateOfBirth", nextValue);
    };

    const handleLinkLine = () => {
        //
        const query = querystring.stringify({
            response_type: "code",
            client_id: lineIntegration?.lineLoginApi?.channelID || "",
            state: JSON.stringify(state),
            redirect_uri: lineRedirectUrl,
        });
        // Build the Line authorize URL.
        const lineUrl = `${LINE_ACCESS_URL}/authorize?scope=${LINE_SCOPE}&${query}`;
        window.location.href = lineUrl;
    };

    return (
        <>
            <RenderMeta
                title={t("meta.user_setting", { name: values.fullName || t("meta.user") })}
            />
            {title ? (
                <ComponentUserSettingSave
                    values={values}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    handleSubmit={handleSubmit}
                    isSubmitting={isSubmitting}
                    errors={errors}
                    touched={touched}
                    title={title}
                    setFieldTouched={setFieldTouched}
                    errorPhone={errorPhone || ""}
                    handleChangeDate={handleChangeDate}
                    setFieldValue={setFieldValue}
                    setError={setError}
                    handleChangeTitle={handleChangeTitle}
                />
            ) : (
                <ComponentUserSetting
                    handleUploadImage={handleUploadImage}
                    handleChange={handleChange}
                    values={values}
                    handleChangeTitle={handleChangeTitle}
                    handleLinkLine={handleLinkLine}
                    handleLogout={handleLogout}
                    handleChangeName={handleChangeName}
                    email={email}
                    setEmail={setEmail}
                />
            )}
        </>
    );
};
